
import React from 'react';
import { Calendar, DollarSign, MapPin, Music, TrendingUp, Users, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, LineChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { mockGigs } from './mockGigData';
import { Link } from 'react-router-dom';

const GigAnalytics: React.FC = () => {
  // Calculate metrics
  const totalGigs = mockGigs.length;
  const confirmedGigs = mockGigs.filter(gig => gig.status === 'confirmed').length;
  const totalRevenue = mockGigs.reduce((sum, gig) => sum + gig.payment, 0);
  const avgAttendance = mockGigs.reduce((sum, gig) => sum + gig.capacity, 0) / totalGigs;
  
  // Prepare chart data
  const monthlyGigData = [
    { name: 'Jan', gigs: 2, revenue: 800 },
    { name: 'Feb', gigs: 3, revenue: 1200 },
    { name: 'Mar', gigs: 1, revenue: 500 },
    { name: 'Apr', gigs: 4, revenue: 1800 },
    { name: 'May', gigs: 2, revenue: 1000 },
    { name: 'Jun', gigs: 5, revenue: 2500 },
    { name: 'Jul', gigs: 6, revenue: 3000 },
    { name: 'Aug', gigs: 3, revenue: 1500 },
    { name: 'Sep', gigs: 4, revenue: 2200 },
    { name: 'Oct', gigs: 7, revenue: 3500 },
    { name: 'Nov', gigs: 5, revenue: 2800 },
    { name: 'Dec', gigs: 8, revenue: 4000 },
  ];
  
  const venueTypeData = [
    { name: 'Clubs', value: 35 },
    { name: 'Festivals', value: 15 },
    { name: 'Private Events', value: 25 },
    { name: 'Concert Halls', value: 10 },
    { name: 'Bars', value: 15 },
  ];
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Performance Analytics</h2>
        <Link 
          to="/analytics" 
          className="text-sm text-primary hover:underline flex items-center gap-1"
        >
          View full analytics <ExternalLink className="h-3.5 w-3.5" />
        </Link>
      </div>
      
      {/* Metrics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Gigs</p>
                <h3 className="text-2xl font-bold mt-1">{totalGigs}</h3>
              </div>
              <Calendar className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              <span className="text-green-500">+12%</span> from last year
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Confirmed Bookings</p>
                <h3 className="text-2xl font-bold mt-1">{confirmedGigs}</h3>
              </div>
              <Music className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              <span className="text-green-500">{(confirmedGigs/totalGigs*100).toFixed(0)}%</span> confirmation rate
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Revenue</p>
                <h3 className="text-2xl font-bold mt-1">${totalRevenue.toLocaleString()}</h3>
              </div>
              <DollarSign className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              <span className="text-green-500">+18%</span> from last year
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg. Attendance</p>
                <h3 className="text-2xl font-bold mt-1">{Math.round(avgAttendance)}</h3>
              </div>
              <Users className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              <span className="text-green-500">+5%</span> from last year
            </p>
          </CardContent>
        </Card>
      </div>
      
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Performance Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Monthly Performance</CardTitle>
            <CardDescription>Number of gigs and revenue</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={monthlyGigData}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" opacity={0.2} />
                  <XAxis dataKey="name" tick={{ fill: '#aaa' }} />
                  <YAxis yAxisId="left" tick={{ fill: '#aaa' }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: '#aaa' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#222', 
                      border: '1px solid #444',
                      borderRadius: '8px',
                      color: '#fff'
                    }} 
                  />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="gigs"
                    stroke="#9b87f5"
                    strokeWidth={2}
                    dot={{ r: 4, fill: '#9b87f5', strokeWidth: 0 }}
                    activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2 }}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="revenue"
                    stroke="#f97316"
                    strokeWidth={2}
                    dot={{ r: 4, fill: '#f97316', strokeWidth: 0 }}
                    activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        
        {/* Venue Type Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Venue Distribution</CardTitle>
            <CardDescription>Breakdown by venue type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={venueTypeData}
                  layout="vertical"
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" opacity={0.2} />
                  <XAxis type="number" tick={{ fill: '#aaa' }} />
                  <YAxis dataKey="name" type="category" tick={{ fill: '#aaa' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#222', 
                      border: '1px solid #444',
                      borderRadius: '8px',
                      color: '#fff'
                    }} 
                  />
                  <Bar dataKey="value" fill="#9b87f5" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Insights */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Insights</CardTitle>
          <CardDescription>AI-powered recommendations to improve your bookings</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg border bg-card/50">
              <TrendingUp className="h-5 w-5 text-green-500 mb-2" />
              <h4 className="font-medium">Revenue Growth</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Your average gig fee has increased by 15% in the last 3 months. Consider raising your rate for new bookings.
              </p>
            </div>
            
            <div className="p-4 rounded-lg border bg-card/50">
              <MapPin className="h-5 w-5 text-blue-500 mb-2" />
              <h4 className="font-medium">Location Opportunity</h4>
              <p className="text-sm text-muted-foreground mt-1">
                You perform 60% more in the downtown area. Consider reaching out to venues in the east side to diversify.
              </p>
            </div>
            
            <div className="p-4 rounded-lg border bg-card/50">
              <Users className="h-5 w-5 text-purple-500 mb-2" />
              <h4 className="font-medium">Audience Growth</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Festivals have brought you 40% more new audience members. Focus on booking more festival gigs next season.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GigAnalytics;
